import { Add } from '@mui/icons-material';
import { Box, Button, Chip, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { formatRecordDateTime } from '../../../library/dateTimeHelper';
import { bookAppointmentLink, myCalendarLink, CalendarLocationState } from '../../../library/constants';
import {
    AppointmentStatus,
    canReschedule,
    canWithdraw,
    IAppointment,
} from '../../../library/models/appointment';
import { IPage } from '../../../library/models/page';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactInfiniteQuery, useReactMutation } from '../../../services/queryService';
import { splitApiError } from '../../../library/useApiFormError';
import { useRefreshDiary } from '../../../library/useRefreshDiary';
import { ConfirmationDialog } from '../../confirmationDialog/confirmationDialog';
import { useSnackbar } from '../../layout/snackbarProvider';
import { services } from '../../../services/services';
import { useAuth } from '../../../contexts/AuthContext';
import { AppointmentDetailDialog } from '../../appointmentDetail/AppointmentDetailDialog';
import { RescheduleDialog } from '../../appointmentDetail/RescheduleDialog';
import {
    emptyStateStyles,
    floatingBookActionStyles,
    headerBoxStyles,
    listStyles,
    loadingBoxStyles,
    requestNoteStyles,
    rowActionsStyles,
    rowActionStyles,
    rowStyles,
    sectionTitleStyles,
    showMoreBoxStyles,
    statusChipStyles,
    statusFieldStyles,
    subtitleStyles,
    titleStyles,
    whenStyles,
    withStyles,
} from './myAppointments.styles';
import { localised } from '../../../library/models/localisedText';

/** How many rows a page holds. A "show more" fetches the next one. */
const PAGE_SIZE = 20;

/** The dropdown's own value for "don't narrow at all", which sends no `statuses` param. */
const ALL_STATUSES = 'ALL';

type StatusFilter = AppointmentStatus | typeof ALL_STATUSES;

/**
 * What the *past* list can be narrowed to. A pending request is always upcoming, so it is not
 * one of them; a declined one is history like a cancellation.
 */
const STATUS_OPTIONS: AppointmentStatus[] = ['SCHEDULED', 'HELD', 'NO_SHOW', 'CANCELLED', 'DECLINED'];

const STATUS_LABELS: Record<AppointmentStatus, string> = {
    SCHEDULED: ResourcesIds.APPOINTMENT_STATUS_SCHEDULED,
    HELD: ResourcesIds.APPOINTMENT_STATUS_HELD,
    NO_SHOW: ResourcesIds.APPOINTMENT_STATUS_NO_SHOW,
    CANCELLED: ResourcesIds.APPOINTMENT_STATUS_CANCELLED,
    REQUESTED: ResourcesIds.APPOINTMENT_STATUS_REQUESTED,
    DECLINED: ResourcesIds.APPOINTMENT_STATUS_DECLINED,
};

/** A request is `info`: nothing is wrong with it, it is simply waiting for an answer. */
const STATUS_COLOURS: Record<AppointmentStatus, 'default' | 'success' | 'warning' | 'info'> = {
    SCHEDULED: 'default',
    HELD: 'success',
    NO_SHOW: 'warning',
    CANCELLED: 'default',
    REQUESTED: 'info',
    DECLINED: 'default',
};

/**
 * Both ends of the booking read the same page.
 *
 * It is gated to careRoles rather than to customers alone, and the API answers it from
 * whichever end the caller is on: a patient sees the appointments they are attending, a
 * therapist the ones they are giving. Neither can ask for anybody else's.
 *
 * Cancelled and missed appointments are shown rather than filtered out, and this is now
 * the only page that shows them: My Calendar draws what occupies time, so a booking that
 * was called off leaves the grid and lands here. A patient looking for "what happened to
 * that Tuesday" needs to find it, and a no-show they can see is the only kind they can
 * query.
 *
 * **A therapist can now act on what they find**, through the same dialog My Calendar
 * opens -- which is the point of the page rather than a convenience. The calendar draws
 * only what occupies time, so reopening a booking cancelled by mistake meant turning on
 * *Show cancelled* and knowing which day to look at; and yesterday's unresolved session
 * was listed here with no way to mark it held. Both are one press now.
 *
 * **Resolving is still the therapist's alone**, and the dialog gates it: `PUT
 * /appointments/{id}/status` is THERAPIST/ADMIN, so the Cancel button this page once
 * showed every visitor answered a patient with "the appointment could not be updated" --
 * a button that had never worked.
 *
 * **Moving one is not.** A patient may change the time of any scheduled session they are
 * in, whoever booked it, choosing from windows the therapist is genuinely free for --
 * `canReschedule` decides it and the API decides it the same way. Their copy of this page
 * is therefore no longer only a history.
 *
 * **A patient's own booking is a request until the therapist confirms it**, and it shows
 * here from the moment it is sent: in *Upcoming*, with the chip saying so and a line under
 * it naming whose answer it waits for and by when. Such a row carries a second verb,
 * *Withdraw request*, which is the one way a patient can call anything off -- and only this,
 * only before it is answered. A declined one lands in *Past* with the therapist's note, or
 * the plain fact that nobody answered in time.
 *
 * That rule keyed on who *booked* the appointment at first, and this screen is what argued
 * it down: it made a patient's list two kinds of visually identical row, one pressable and
 * one inert, with nothing on either to say why. What made widening it safe is that a move
 * now emails both people rather than happening silently.
 *
 * **The verb sits on the row, not behind it, and the two roles differ for a reason.** A
 * patient has exactly one thing they can do, so a dialog holding a single button is a step
 * that buys nothing; staff have five, which is a dialog's worth. See `row` below for why
 * that also decides which of the two is a `role="button"`.
 *
 * **Nothing here is filtered or split in the browser.** The two sections are two paged
 * requests, the status dropdown reaches the SQL, and the boundary between them is the
 * clinic's clock rather than the viewer's. It used to be a year fetched whole and cut up
 * with `Array.filter`, which paid for twelve months to draw a screenful, silently lost
 * anything older, and put rows near midnight on the wrong side of the split.
 *
 * Blocks of leave are the one thing left out, and the API leaves them out. They belong to
 * nobody, they sit at SCHEDULED for ever -- so they would pile up in "upcoming" and never
 * leave -- and *Cancel* is the wrong verb for one: a block is removed outright, on the
 * calendar that drew it.
 */
export const MyAppointments = (): JSX.Element => {
    const { t: translate, i18n } = useTranslation();
    const navigate: NavigateFunction = useNavigate();
    const { user, role } = useAuth();

    const isTherapist: boolean = role === 'THERAPIST';

    /** The same distinction the dialog and the API make, from the same place. */
    const isStaff: boolean = role === 'THERAPIST' || role === 'ADMIN';

    /**
     * Whether this viewer may move this row, which for a patient is the only thing they can
     * do to one and is therefore drawn on the row itself.
     */
    const canMove = (appointment: IAppointment): boolean =>
        canReschedule(appointment, user?.id ?? 0, isStaff);

    const [status, setStatus] = useState<StatusFilter>(ALL_STATUSES);
    /** Which appointment the detail dialog is about, or null when it is closed. */
    const [selected, setSelected] = useState<IAppointment | null>(null);
    /**
     * Which appointment is being moved from a row, or null.
     *
     * Held apart from `selected` because a patient reaches the move dialog *directly* — the
     * detail dialog is never opened on their way there, so there is no selection to carry.
     */
    const [moving, setMoving] = useState<IAppointment | null>(null);

    /** The request the patient has asked to take back, while they confirm they mean it. */
    const [withdrawing, setWithdrawing] = useState<IAppointment | null>(null);

    const { showSnackbar } = useSnackbar();
    const refreshDiary = useRefreshDiary();
    const withdraw = useReactMutation(services.appointments.withdrawRequestMutation());

    /**
     * The same button, but the two ends of an appointment create one in different places.
     *
     * A customer books: '/appointments' is theirs and asks them to pick a therapist. A
     * therapist does not book themselves a session -- they add it to their own day -- and
     * '/appointments' is gated to customerRoles, so sending them there only had the guard
     * bounce them to their landing page. They go to their diary with its new-entry dialog
     * already open, which is the same press in the place that can answer it.
     */
    const startBooking = (): void => {
        if (isTherapist) {
            const state: CalendarLocationState = { openNewEntry: true };

            navigate(myCalendarLink, { state });

            return;
        }

        navigate(bookAppointmentLink);
    };

    /*
     * Two requests, because they are two questions the server answers differently -- one
     * ascending from now, one descending into the past -- and because each pages on its
     * own. The status filter is part of the past query's key, so changing it is a fresh
     * request rather than a re-render over rows already held.
     */
    const upcoming = useReactInfiniteQuery<IAppointment>(
        services.appointments.getAppointmentHistory(PAGE_SIZE, { scope: 'UPCOMING' })
    );

    const past = useReactInfiniteQuery<IAppointment>(
        services.appointments.getAppointmentHistory(PAGE_SIZE, {
            scope: 'PAST',
            statuses: status === ALL_STATUSES ? undefined : [status],
        })
    );

    const rowsOf = (pages?: { pages: IPage<IAppointment>[] }): IAppointment[] =>
        pages?.pages.flatMap(page => page.items) ?? [];

    const upcomingRows: IAppointment[] = rowsOf(upcoming.data);
    const pastRows: IAppointment[] = rowsOf(past.data);

    /** Every page reports the same total, so the first one answers for all of them. */
    const remainingOf = (
        pages: { pages: IPage<IAppointment>[] } | undefined,
        shown: number
    ): number => Math.max((pages?.pages[0]?.totalItems ?? 0) - shown, 0);

    /**
     * Reopening a cancelled booking moves it from Past to Upcoming, and cancelling one
     * moves it the other way -- so both lists are stale after any action, whichever list
     * the row was pressed in.
     */
    const refetchBoth = (): void => {
        upcoming.refetch();
        past.refetch();
    };

    const handleWithdraw = (): void => {
        if (!withdrawing) {
            return;
        }

        withdraw.mutate(withdrawing.id, {
            onSuccess: () => {
                showSnackbar(translate(ResourcesIds.APPOINTMENT_WITHDRAWN), 'success');
                setWithdrawing(null);
                refreshDiary();
            },
            onError: (error: unknown) => {
                setWithdrawing(null);
                showSnackbar(
                    splitApiError(error, [], translate,
                        ResourcesIds.APPOINTMENT_WITHDRAW_FAILED).formError
                        ?? translate(ResourcesIds.APPOINTMENT_WITHDRAW_FAILED),
                    'error'
                );
            },
        });
    };

    /**
     * Where a request or a decline stands, in a line under the row: whose answer it is waiting
     * for and by when, or why it was declined. The chip says *that* it is waiting; this says
     * what the patient most wants to know next.
     */
    const requestNote = (appointment: IAppointment): string | null => {
        if (appointment.status === 'REQUESTED' && appointment.confirmBy) {
            const when: string = formatRecordDateTime(appointment.confirmBy, i18n);

            return isStaff
                ? translate(ResourcesIds.APPOINTMENT_REQUEST_RESPOND_BY, { when })
                : translate(ResourcesIds.APPOINTMENT_REQUEST_AWAITING, {
                    name: appointment.therapistName,
                    when,
                });
        }

        if (appointment.status === 'DECLINED') {
            if (appointment.decisionNote) {
                return translate(ResourcesIds.APPOINTMENT_DECLINED_NOTE, {
                    note: appointment.decisionNote,
                });
            }

            return translate(appointment.declinedAutomatically
                ? ResourcesIds.APPOINTMENT_DECLINED_AUTOMATICALLY
                : ResourcesIds.APPOINTMENT_DECLINED_PLAIN);
        }

        return null;
    };

    /**
     * Whoever the row is *not* about.
     *
     * The page reads from both ends of the same booking, so the useful name is the other
     * one: a patient is told which therapist, and a therapist which patient. It used to
     * print the therapist either way, which meant a therapist's own history was a list of
     * their own name.
     */
    const otherParty = (appointment: IAppointment): string | null | undefined =>
        isTherapist ? appointment.patientName : appointment.therapistName;

    /**
     * One action goes on the row; several stay behind a click-through.
     *
     * A patient can do exactly one thing to an appointment -- move it -- so a dialog whose
     * whole content is a single button is a step that buys nothing: two clicks and an
     * overlay to reach one verb. Staff have five (*Mark as held*, *Did not attend*,
     * *Cancel*, *Reopen*, *Change time*), which do not go on a row, so their row stays a
     * click-through and the dialog earns its place.
     *
     * **A row is a button, or it contains one -- never both.** A `<Button>` inside a
     * `role="button"` row is a nested interactive control: invalid, announced wrongly by a
     * screen reader, and needing `stopPropagation` to stop one press doing two things. So
     * the patient's row gives up being pressable in order to carry the action, and
     * `rowStyles` is told so -- otherwise it keeps a pointer cursor and a hover lift that
     * no longer lead anywhere.
     *
     * Deliberately no inline button for staff beside the dialog's own: one verb in two
     * places is how the two drift.
     */
    const row = (appointment: IAppointment, isPast: boolean): JSX.Element => (
        <Box
            key={appointment.id}
            sx={rowStyles(isPast, isStaff)}
            {...(isStaff
                ? {
                    role: 'button',
                    tabIndex: 0,
                    onClick: () => setSelected(appointment),
                }
                : {})}
        >
            <Box>
                <Typography sx={whenStyles}>
                    {formatRecordDateTime(appointment.startsAt, i18n)}
                    <Chip
                        size="small"
                        sx={statusChipStyles}
                        color={STATUS_COLOURS[appointment.status]}
                        label={translate(STATUS_LABELS[appointment.status])}
                    />
                </Typography>
                <Typography sx={withStyles}>
                    {[appointment.serviceNameRo
                        && localised(appointment.serviceNameRo, appointment.serviceNameEn, i18n.language),
                    otherParty(appointment)]
                        .filter(Boolean)
                        .join(' · ')}
                </Typography>
                {requestNote(appointment) && (
                    <Typography sx={requestNoteStyles}>{requestNote(appointment)}</Typography>
                )}
            </Box>

            {/* A pending request carries a second verb, and only a patient's row carries any:
                taking the request back before it is answered. Not a cancel button -- once it
                is confirmed, calling it off is still the clinic's to do. */}
            {!isStaff && (canMove(appointment) || canWithdraw(appointment, user?.id ?? 0)) && (
                <Box sx={rowActionsStyles}>
                    {canMove(appointment) && (
                        <Button
                            size="small"
                            variant="outlined"
                            sx={rowActionStyles}
                            onClick={() => setMoving(appointment)}
                        >
                            {translate(ResourcesIds.APPOINTMENT_RESCHEDULE)}
                        </Button>
                    )}
                    {canWithdraw(appointment, user?.id ?? 0) && (
                        <Button
                            size="small"
                            color="error"
                            sx={rowActionStyles}
                            onClick={() => setWithdrawing(appointment)}
                        >
                            {translate(ResourcesIds.APPOINTMENT_WITHDRAW)}
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );

    const showMore = (
        query: { hasNextPage: boolean; fetchNextPage: () => unknown; isFetchingNextPage: boolean },
        remaining: number
    ): JSX.Element | null =>
        query.hasNextPage ? (
            <Box sx={showMoreBoxStyles}>
                <Button
                    disabled={query.isFetchingNextPage}
                    onClick={() => query.fetchNextPage()}
                >
                    {translate(ResourcesIds.MY_APPOINTMENTS_SHOW_MORE, { remaining })}
                </Button>
            </Box>
        ) : null;

    return (
        <>
            <Box sx={headerBoxStyles}>
                <Typography variant="h4" component="h1" gutterBottom sx={titleStyles}>
                    {translate(ResourcesIds.MY_APPOINTMENTS)}
                </Typography>
                <Typography sx={subtitleStyles}>
                    {translate(ResourcesIds.MY_APPOINTMENTS_SUBTITLE)}
                </Typography>
            </Box>

            <Box sx={floatingBookActionStyles}>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={startBooking}
                >
                    {translate(ResourcesIds.BOOK_APPOINTMENT_BUTTON)}
                </Button>
            </Box>

            {upcoming.isLoading ? (
                <Box sx={loadingBoxStyles}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Typography sx={sectionTitleStyles}>
                        {translate(ResourcesIds.MY_APPOINTMENTS_UPCOMING)}
                    </Typography>

                    {upcomingRows.length === 0 ? (
                        <Typography sx={emptyStateStyles}>
                            {translate(ResourcesIds.MY_APPOINTMENTS_NONE_UPCOMING)}
                        </Typography>
                    ) : (
                        <Box sx={listStyles}>{upcomingRows.map(a => row(a, false))}</Box>
                    )}

                    {showMore(upcoming, remainingOf(upcoming.data, upcomingRows.length))}

                    <Typography sx={sectionTitleStyles}>
                        {translate(ResourcesIds.MY_APPOINTMENTS_PAST)}
                    </Typography>

                    <TextField
                        select
                        size="small"
                        sx={statusFieldStyles}
                        label={translate(ResourcesIds.MY_APPOINTMENTS_STATUS_FILTER_LABEL)}
                        value={status}
                        onChange={event => setStatus(event.target.value as StatusFilter)}
                    >
                        <MenuItem value={ALL_STATUSES}>
                            {translate(ResourcesIds.MY_APPOINTMENTS_STATUS_FILTER_ALL)}
                        </MenuItem>
                        {STATUS_OPTIONS.map(option => (
                            <MenuItem key={option} value={option}>
                                {translate(STATUS_LABELS[option])}
                            </MenuItem>
                        ))}
                    </TextField>

                    {/* Two different nothings, and the filter is what tells them apart: the
                        server has already narrowed, so an empty answer to "cancelled only"
                        and an empty history look identical on the wire. */}
                    {pastRows.length === 0 ? (
                        <Typography sx={emptyStateStyles}>
                            {translate(status === ALL_STATUSES
                                ? ResourcesIds.MY_APPOINTMENTS_NONE_PAST
                                : ResourcesIds.MY_APPOINTMENTS_NO_MATCHES)}
                        </Typography>
                    ) : (
                        <Box sx={listStyles}>{pastRows.map(a => row(a, true))}</Box>
                    )}

                    {showMore(past, remainingOf(past.data, pastRows.length))}
                </>
            )}

            {/* Every verb My Calendar has, on whichever row was pressed. Staff only: a
                patient's rows are not pressable, because their one verb is on the row. */}
            <AppointmentDetailDialog
                appointment={selected}
                onClose={() => setSelected(null)}
                onChanged={refetchBoth}
            />

            {/* The patient's path, reached straight from the row rather than through the
                dialog above. `isStaff` is false by construction here -- the button that
                opens this is drawn only for a patient -- so this is always the slot
                picker, which is what a patient should be choosing from. */}
            <RescheduleDialog
                appointment={moving}
                isStaff={false}
                onClose={() => setMoving(null)}
                onMoved={() => {
                    setMoving(null);
                    refetchBoth();
                }}
            />

            <ConfirmationDialog
                isOpen={!!withdrawing}
                onClose={() => setWithdrawing(null)}
                onConfirm={handleWithdraw}
                title={translate(ResourcesIds.APPOINTMENT_WITHDRAW_TITLE)}
                description={translate(ResourcesIds.APPOINTMENT_WITHDRAW_DESCRIPTION, {
                    name: withdrawing?.therapistName ?? '',
                })}
                confirmMessage={translate(ResourcesIds.APPOINTMENT_WITHDRAW)}
                denyMessage={translate(ResourcesIds.APPOINTMENT_WITHDRAW_KEEP)}
            />
        </>
    );
};
